import { CodigoQr, Marca, linhasDoToken, quantidadeComUnidade } from "@plantus/ui";
import type { Lote, VolumeDaEtiqueta } from "@plantus/api-client";

import { usarAmbiente } from "../../../nucleo/ambiente/ProvedorDeAmbiente";

import "./etiqueta.css";

/* =====================================================================
   O endereço que vai impresso — ADR-0006
   ---------------------------------------------------------------------
   ⚠️ O DOMÍNIO NÃO É DADO. Ele não está no banco, não está no lote e não
   está escrito neste arquivo: é composto AQUI, na renderização, a partir
   de configuração (ADR-0006 item 1). Trocar de domínio é trocar uma
   variável de ambiente, não migrar dado — nenhuma coluna contém
   "https://".

   Sem a variável, vale a origem da própria página. Em desenvolvimento é o
   que se quer; em produção o Apache serve tela e API no mesmo domínio, e
   a origem JÁ É o endereço público.
   ===================================================================== */
function basePublica(): string {
  const configurada = import.meta.env["VITE_PLANTUS_URL_PUBLICA_BASE"] as string | undefined;
  if (configurada !== undefined && configurada !== "") {
    return configurada.replace(/\/+$/, "");
  }
  return typeof window === "undefined" ? "" : window.location.origin;
}

/**
 * O endereço impresso — **ADR-0006 item 2, o resolvedor**.
 *
 * ```
 * <base>/qr/23d96d7d3bd84ead8b8d354dac0aef74
 *        │
 *        └── 302 ──► /producao/materias-primas/volume/23d96d7d…0aef74
 * ```
 *
 * **Este é um compromisso permanente**, e a única razão de ele poder
 * existir é que o que vai no papel **não é a rota da aplicação**. A rota
 * interna pode ser renomeada amanhã; o endereço colado no tambor, não —
 * e o modo de falha dele é silencioso: ninguém do grupo percebe, quem
 * descobre é quem escaneou.
 *
 * **O token é do VOLUME, um por bombona.** Foi o pedido do PO, e ele
 * muda o que o símbolo responde: escanear o tambor 7 de 20 abre a ficha
 * **daquele** recipiente, com o peso dele e o número dele — não a ficha
 * do lote com uma média. Vinte tambores, vinte QR diferentes.
 *
 * **Por que opaco.** Um identificador derivado do lote —
 * `/qr/MP-COP-382684/7` — seria adivinhável, e adivinhável significa
 * **enumerável**: quem tivesse um único adesivo varreria os lotes do
 * grupo pela barra de endereço. O argumento inteiro está em
 * `TokenDeVolume.java`.
 *
 * **O caminho manual continua existindo**, e é por isso que o código do
 * lote segue impresso em letra grande logo acima do QR: ele resolve
 * sozinho (`LoteComoAlvoPublico`) e leva à ficha do lote. Perde-se qual
 * volume era — que é a informação que se perdeu junto com o adesivo.
 *
 * **O domínio continua não sendo dado** (item 1): a base vem de
 * `basePublica()`, acima. O que mudou é só o caminho.
 *
 * **⚠️ O símbolo INCHOU na `V14`, e o custo está medido.** O token
 * passou de 8 caracteres para 32 hexadecimais (ADR-0006, a emenda de
 * 2026-08-14): num domínio de 34 caracteres a URL vai de 46 para **70
 * bytes**, e o QR sobe da versão 4 (33 módulos) para a **6 (41)**. Num
 * espaço fixo de papel, versão maior significa **módulo menor** — medido
 * na folha A4, com o QR em 44 mm: **0,98 mm → 0,90 mm**. Continua muito
 * acima do piso prático de 0,5 mm para leitura por celular, e **o
 * gerador não muda uma linha**: ele escolhe a versão sozinho até a 10.
 */
function enderecoDoVolume(token: string): string {
  return `${basePublica()}/qr/${token}`;
}

/**
 * O selo da família: retângulo preto com a sigla **vazada**.
 *
 * Vazar em SVG, e não pintar com CSS, é o que garante que ele apareça no
 * papel — navegador não imprime `background` sem "Gráficos de plano de fundo"
 * ligado, e o padrão do Chrome é desligado.
 *
 * O tamanho é o ponto. A exigência era que a família **salte** sem depender de
 * ler três caracteres pequenos: a sigla ocupa 86 % da altura do selo, o que a
 * torna a segunda coisa maior da etiqueta, atrás só do código. Com 74 % ela
 * ficava com quase a mesma altura de maiúscula do nome do material — ou seja, a
 * família não saltava, que era a única coisa que o selo tinha de fazer.
 */
function Selo({ sigla }: { sigla: string }) {
  const larg = 13.5;
  const alt = 7.2;
  const corpo = alt * 0.86;
  return (
    <svg
      className="et__selo"
      viewBox={`0 0 ${larg} ${alt}`}
      role="img"
      aria-label={`Família ${sigla}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width={larg} height={alt} fill="#000" />
      <text
        x={larg / 2}
        y={alt / 2}
        fill="#fff"
        fontSize={corpo}
        fontWeight={700}
        letterSpacing={corpo * 0.04}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="Liberation Sans, Arial, sans-serif"
      >
        {sigla}
      </text>
    </svg>
  );
}

/**
 * A etiqueta de identificação de material.
 *
 * <p><b>Ela identifica; ela não decide.</b> Nome, lote, fornecedor, quantidade,
 * validade — nada ali é resultado de julgamento, e por isso ela não precisa de
 * assinatura.
 *
 * <h2>🔴 A situação NÃO sai no papel — decisão do PO, 2026-08-13</h2>
 *
 * <p>Havia aqui uma faixa preta com <b>QUARENTENA</b>, e ela foi <b>removida</b>.
 * O raciocínio é curto e fecha: <b>a etiqueta não é trocada quando o lote é
 * liberado.</b> Ninguém recolhe vinte tambores para trocar adesivo. Logo, uma
 * etiqueta que afirma <b>QUARENTENA</b> vira <b>afirmação falsa</b> no dia da
 * aprovação — e etiqueta que mente é pior que etiqueta que cala, porque o
 * operador confia nela.
 *
 * <p>A faixa era o único pedaço deste documento que <b>decidia</b>, e era
 * justamente o que exigiria assinatura para poder existir. Tirá-la não
 * contradiz "ela identifica; ela não decide": completa a frase.
 *
 * <p><b>O que substitui o controle:</b> a <b>segregação física</b> — área de
 * quarentena separada — e o <b>QR</b>, que abre a situação atual no celular. A
 * situação viva mora no sistema, que é o único lugar onde ela pode ser
 * <i>atualizada</i>.
 *
 * <p>🔴 <b>Pendência declarada:</b> o <b>POP da área de quarentena não existe
 * ainda</b>, e esta decisão se apoia nele. Ver
 * {@code docs/backend/producao.md}.
 *
 * <p>⚠️ A <b>tela</b> continua mostrando a situação: a ficha "O que vai para o
 * papel", em {@code PaginaEtiquetas.tsx}, traz o {@code Selo}. Conferir antes
 * de imprimir é o passo que existe para não se descobrir nada com a folha na
 * mão. O que saiu é a situação <b>no papel</b>.
 *
 * <h2>O que mudou no desenho, e por quê</h2>
 *
 * A etiqueta anterior era uma lista de pares rótulo/valor com o código de
 * barras embaixo: tudo com o mesmo peso, e o que se lia primeiro era o nome do
 * material. O levantamento do PO diz o contrário — quem está no galpão precisa,
 * nesta ordem: <b>que família é</b> (três copaíbas na mesma prateleira),
 * <b>qual lote</b>, <b>qual volume e quanto pesa este volume</b>. O desenho
 * novo é essa ordem, e cada degrau de tamanho é um degrau dessa hierarquia.
 *
 * <h2>A MARCA subiu, a marca de AMBIENTE desceu</h2>
 *
 * Duas trocas de lugar pedidas pelo PO, e as duas seguem o mesmo critério: o
 * <b>topo é para identificar o material</b>.
 *
 * <table>
 *   <tr><th></th><th>Antes</th><th>Agora</th></tr>
 *   <tr><td>Símbolo da Plantus</td><td>6,4 mm, na faixa do topo</td>
 *       <td><b>Grande, no canto superior direito</b> — o espaço que sobrava
 *           ao lado do selo da família</td></tr>
 *   <tr><td>{@code SISTEMA EM VALIDAÇÃO}</td><td>Primeira linha do papel</td>
 *       <td><b>Rodapé, em corpo miúdo</b></td></tr>
 * </table>
 *
 * <p>O símbolo continua em <b>uma tinta</b>, e o motivo está medido em
 * {@code marca/README.md} — térmica não tem cinza, laser converte cor em trama
 * que come linha fina, e preto sobre branco dá 21:1 contra 6,92:1 do verde da
 * marca. O que o tamanho novo compra é o contrário disso: o traço, que era
 * fino demais para térmica, passa a caber.
 *
 * <p>🔴 <b>A marca de ambiente não pode sumir</b>, e por isso ela desceu em vez
 * de sair: etiqueta impressa sem ela <b>parece oficial</b>, e vai colada num
 * tambor longe da tela que dizia o contrário. Removê-la por completo é decisão
 * do PO, e não foi tomada. Quem decide se ela sai no papel é o <b>ambiente</b>,
 * pelo mesmo mecanismo da {@code FaixaAmbiente}.
 */
export function Etiqueta({
  lote,
  empresa,
  volume,
}: {
  lote: Lote;
  empresa: string | null;
  /**
   * O recipiente que esta etiqueta identifica — **o registro dele**, não o
   * índice.
   *
   * <p>Ele traz três coisas que a etiqueta não teria como saber sozinha: o
   * <b>número</b> (o `7` de `VOLUME 7/20`), o <b>peso gravado deste
   * recipiente</b> — e não a média do lote — e o <b>token do QR desta
   * etiqueta</b>. Vinte bombonas do mesmo lote produzem vinte etiquetas com
   * vinte símbolos diferentes.
   *
   * <p>🔴 <b>Ausente quando o lote não tem volume registrado</b>, e aí a
   * etiqueta sai <b>sem QR</b>. Não é degradação silenciosa: sem registro de
   * volume não há token, e um QR apontando para o lote em vez do recipiente
   * seria um símbolo que responde a pergunta de outro tambor. O papel diz o que
   * sabe — `VOLUMES 20 · PESO POR VOLUME`, que é média e se lê como média — e
   * não inventa qual recipiente está na mão.
   */
  volume?: VolumeDaEtiqueta;
}) {
  const numerado = volume !== undefined;
  /*
   * A marca de ambiente existe porque o ambiente não é validado — então quem
   * decide se ela sai no papel é o ambiente, não uma constante neste arquivo.
   * Mesmo mecanismo da `FaixaAmbiente`, e de propósito: um segundo mecanismo
   * seria um segundo lugar para esquecer de atualizar no dia da validação.
   */
  const { ambiente } = usarAmbiente();

  return (
    <div className="et">
      <div className="et__corpo">
        {/* ── o topo: a família à esquerda, a MARCA à direita ──────────
            O canto superior direito estava vazio, e passou a ser da marca
            da Plantus, GRANDE — pedido do PO.

            ⚠️ EM UMA TINTA, e não no verde da marca. A razão não é de
            gosto, e está medida em `marca/README.md`:

              · impressora térmica NÃO TEM CINZA. Cada ponto do cabeçote
                queima ou não queima; um símbolo verde chega ao driver e
                vira meio-tom — que dentro de um traço fino não é traço
                mais claro, é BORRÃO;
              · laser monocromática converte cor em trama, e trama dentro
                de uma linha fina come a linha;
              · preto sobre branco dá 21:1; `#296704` sobre branco dá
                6,92:1 — contraste de graça não se joga fora num documento
                lido encostado num tambor, sob luz de galpão.

            E há a razão de documento, mais forte que as três: a etiqueta é
            a identificação FÍSICA do material e declara as próprias duas
            cores justamente para NÃO depender da identidade visual da
            empresa, que muda. Se a marca trocar de verde amanhã, o adesivo
            já colado não troca junto — e duas linguagens visuais na mesma
            prateleira é pior que nenhuma.

            🔴 O TAMANHO É O QUE MUDOU PARA MELHOR. O traço deste desenho
            tem 3,64 % da altura na mediana e 1,14 % nas pontas das folhas.
            Aos 6,4 mm de antes isso dava 0,23 mm e 0,07 mm — abaixo de um
            ponto de térmica de 203 dpi (0,125 mm), e as pontas sumiam.
            Grande, a conta vira outra; os números medidos estão em
            docs/backend/producao.md. */}
        <div className="et__topo">
          {/* ── a família ───────────────────────────────────────────────
              Lote antigo (MP-0002) não tem família registrada: o selo some
              junto com o dado, em vez de imprimir um quadrado preto vazio.
              Quadrado vazio numa etiqueta de identificação é pior que
              ausência — quem confere não sabe se o dado não existe ou se
              alguém esqueceu de preencher. */}
          <div className="et__familia">
            {lote.tipo !== null && lote.tipo !== "" && <Selo sigla={lote.tipo} />}
            {lote.palavra !== null && lote.palavra !== "" && (
              <span className="et__palavra">{lote.palavra}</span>
            )}
          </div>

          {/* O `<svg>` vem do design system; a escala, em `cqw`, é da
              etiqueta. `Marca` não aceita `className` de propósito — quem
              enquadra é a tela —, e o invólucro é o lugar disso. */}
          <span className="et__marca">
            <Marca formato="simbolo" variante="monocromatica" />
          </span>
        </div>

        {/* A empresa é a DO LOTE, e nunca em branco: campo vazio numa
            etiqueta não diz se o dado não existe ou se alguém esqueceu de
            preencher, e este é o campo que diz de qual CNPJ é o material
            colado no tambor. O estado é NOMEADO, e nomeado de um jeito que
            ninguém cola num tambor sem reparar.

            🔴 Ela tem LINHA PRÓPRIA desde o A6, e o motivo é regulatório:
            espremida ao lado do selo, era a primeira a receber reticências
            — "PLANTUS ÓLEOS E EXT…" é o detentor do material saindo pela
            metade no papel. */}
        <p className="et__empresa">
          {empresa === null || empresa.trim() === "" ? "EMPRESA NÃO IDENTIFICADA" : empresa}
        </p>

        <p className="et__material">{lote.materiaPrima}</p>

        {/* ── o código ──────────────────────────────────────────────────
            O objeto que se lê primeiro, a um metro, com pouca luz. Ele
            ocupa a largura inteira porque é o critério nº 1 da etiqueta:
            tudo o mais cede espaço antes dele. */}
        <p className="et__codigo">{lote.codigo}</p>

        {/* ── volume × peso ────────────────────────────────────────────
            A grade ocupa a LARGURA INTEIRA desde o A6. Na paisagem ela era
            vizinha do QR e o par que mais se lê depois do código dividia
            54 mm com um símbolo — é esse espaço que a altura nova comprou. */}
        <div className="et__dados">
          <div className="et__celula et__celula--vol">
            <span className="et__rotulo">{numerado ? "VOLUME" : "VOLUMES"}</span>
            <span className="et__valor et__valor--vol">
              {volume !== undefined ? (
                <>
                  {volume.numero}
                  <span className="et__barra">/</span>
                  {lote.volumes}
                </>
              ) : (
                lote.volumes
              )}
            </span>
          </div>

          {/* O número vem do REGISTRO DO VOLUME, e é o peso gravado daquele
              recipiente — não mais a média impressa vinte vezes.

              🔴 Ele ainda NASCE da divisão do total: a balança da
              conferência não entrou. A diferença é que ele é gravado, e o
              dia em que a balança entrar corrige este número sem tocar
              nesta etiqueta. Ver docs/backend/producao.md.

              Sem registro de volume o rótulo volta a "PESO POR VOLUME", que
              é média e se lê como média. */}
          <div className="et__celula et__celula--peso">
            <span className="et__rotulo">
              {numerado ? (
                <>
                  PESO D<b>ESTE</b> VOLUME
                </>
              ) : (
                "PESO POR VOLUME"
              )}
            </span>
            <span className="et__valor et__valor--peso">
              {/* `quantidadeComUnidade`, nunca o texto cru do servidor:
                  `120.000000 kg` se lê como CENTO E VINTE MIL em português,
                  e aqui é papel colado no material — não se corrige
                  depois. */}
              {quantidadeComUnidade(
                volume === undefined ? lote.quantidadePorVolume : volume.quantidade,
                lote.unidade,
              )}
            </span>
          </div>
        </div>

        <div className="et__meio">
          <div className="et__coluna">
            {/* O total do lote fica em corpo miúdo, com o número de volumes
                COLADO nele: é o que o identifica como agregado, e não como o
                peso do que se está segurando. Ele e o peso do volume já
                estiveram lado a lado, com o mesmo peso visual — que é o
                contrário do que o levantamento pediu.

                No A6 cada campo é uma ENTRADA EMPILHADA — rótulo em cima,
                valor embaixo, um por linha. Quem confere procura FORNECEDOR
                com o dedo; não lê o parágrafo. */}
            <div className="et__secundario">
              {/* ── O RECIPIENTE, quando ele tem identidade ─────────────
                  ⚠️ MEIA-MEDIDA DECLARADA (ADR-0012 item 6). O código do
                  tambor vai ao papel porque passa no critério do ADR-0011:
                  só vai à etiqueta o que continua verdadeiro enquanto
                  aquele adesivo existir — e, sem transfega, o recipiente
                  daquela ocupação não muda.

                  Ele NÃO substitui a plaqueta. No reuso o adesivo é
                  destruído (Art. 192) e o tambor volta a ser anônimo, que
                  é exatamente o momento em que a identidade é necessária.

                  🔴 Sem vínculo, a linha NÃO SAI — como o lote do
                  fornecedor da fazenda própria. Imprimir "RECIPIENTE"
                  seguido de nada é campo em branco numa etiqueta de
                  identificação, e campo em branco se lê como "o dado
                  existia e alguém esqueceu". */}
              {volume?.recipiente != null && volume.recipiente !== "" && (
                <span>
                  <span className="et__rotulo">RECIPIENTE</span> {volume.recipiente}
                </span>
              )}
              <span>
                <span className="et__rotulo">TOTAL DO LOTE · {lote.volumes} VOL.</span>{" "}
                {quantidadeComUnidade(lote.quantidade, lote.unidade)}
              </span>

              {/* Material da fazenda não tem lote de terceiro, e o rótulo
                  some junto com o dado. Imprimir "LOTE FORN." seguido de
                  nada é campo em branco numa etiqueta de identificação. */}
              {lote.loteDoFornecedor !== null && lote.loteDoFornecedor !== "" && (
                <span>
                  <span className="et__rotulo">LOTE DO FORNECEDOR</span> {lote.loteDoFornecedor}
                </span>
              )}
              <span>
                <span className="et__rotulo">FORNECEDOR</span> {lote.fornecedor}
              </span>
              <span>
                <span className="et__rotulo">RECEBIDO</span> {formatar(lote.recebidoEm)}
              </span>
              <span className={lote.vencido ? "et__vencido" : undefined}>
                <span className="et__rotulo">VALIDADE</span> {formatar(lote.validade)}
                {lote.vencido && " · VENCIDO"}
              </span>
            </div>
          </div>

          {/* ── o QR ────────────────────────────────────────────────────
              UM POR VOLUME. Ele carrega o token daquela bombona, e não o
              código do lote: escanear o tambor 7 de 20 abre a ficha DELE,
              com o peso dele. Vinte etiquetas, vinte símbolos diferentes.

              Nível Q desde o resolvedor. Ele era M porque a URL do MVP tinha
              ~150 caracteres e forçava a versão 8 (49 módulos): num espaço
              fixo, versão maior significa MÓDULO MENOR, e é o módulo — não a
              redundância — que decide se um leitor de galpão abre o código.

              ⚠️ DESDE A `V14` A URL TEM 70 BYTES e o símbolo é VERSÃO 6 —
              41 módulos, 49 com a zona de silêncio, contra os 33/41 do token
              de oito caracteres. O `viewBox` do `<svg>` mudou junto, de
              `0 0 41 41` para `0 0 49 49`, e NENHUM CSS depende desse
              número: `.et__qr` é `width: 100%`, a altura vem do `viewBox`
              pela razão de aspecto e o tamanho no papel mora em `--et-qr`.
              Medido na folha A4 (QR de 44 mm): o módulo caiu de 0,98 mm para
              0,90 mm, e o símbolo continua ocupando exatamente 50 % da
              largura da etiqueta.

              🔴 SEM VOLUME, SEM QR. Lote sem recipiente registrado não tem
              token, e um símbolo apontando para o lote inteiro responderia a
              pergunta de outro tambor. O papel prefere calar.

              ⚠️ O TAMANHO DELE MORA NO CSS (`--et-qr`), e não aqui. Este
              `<svg>` é `width: 100%`: sem largura definida no bloco que o
              contém, o navegador aplica o padrão de elemento substituído —
              300 px, 79 mm — e o símbolo fica MAIOR que a etiqueta. Foi
              exatamente esse o defeito que cobriu VOLUME, PESO e
              QUARENTENA. */}
          {volume !== undefined && (
            <div className="et__qrbloco">
              <CodigoQr
                className="et__qr"
                valor={enderecoDoVolume(volume.token)}
                nivel="Q"
                rotulo={
                  `Leia para abrir a situação atual do volume ${volume.numero} de ` +
                  `${lote.volumes} do lote ${lote.codigo}`
                }
              />
              {/* 🔴 O TOKEN EM TEXTO — E O PAPEL DELE MUDOU NA `V14`.
                  Ele era o que se digitava com o adesivo na frente. Não é
                  mais: ninguém redigita 32 hexadecimais de um adesivo sujo
                  de óleo, e fingir que sim seria pior que admitir. O que
                  ele faz hoje é CONFERÊNCIA — comparar, glifo a glifo, o
                  que está no tambor com o que está na tela.

                  🔴 O CAMINHO MANUAL É O CÓDIGO DO LOTE, gigante lá em
                  cima (ADR-0006; feature 1.2.2 R13). Ele leva à ficha do
                  lote — sem saber qual dos vinte, que é a informação
                  perdida junto com o adesivo. Degradação, não beco sem
                  saída.

                  O agrupamento é de QUATRO EM QUATRO, oito grupos
                  uniformes — e não o 8-4-4-4-12 canônico do UUID: o olho
                  conta de quatro em quatro sem pensar, e agrupamento
                  irregular é convenção de máquina. A regra mora em
                  `packages/ui/src/token.ts`, num lugar só, e é a mesma de
                  `TokenDeVolume.formatado()` no servidor — que NÃO a
                  entrega pronta: `GET /lotes/{id}/volumes` devolve o token
                  cru, como devolve a quantidade crua.

                  A QUEBRA EM DUAS LINHAS É DECIDIDA AQUI, e não pelo
                  navegador: deixar o texto dobrar sozinho põe o corte onde
                  a largura mandar — no meio de um grupo, e num lugar na
                  etiqueta A6 nominal e em outro na reduzida da folha A4.
                  Duas etiquetas do mesmo lote com o token cortado em
                  pontos diferentes é o contrário do que o agrupamento
                  existe para fazer.

                  O hífen custa um caractere de tinta e nenhum de dado: ele
                  não entra no símbolo, e a leitura do servidor o descarta
                  (`TokenDeVolume.normalizar`). */}
              <p className="et__url">
                {/* A chave é o índice, e aqui ela é o certo: a lista é
                    derivada de um único valor imutável, tem ordem fixa e nunca
                    é reordenada nem filtrada. Chavear pelo conteúdo quebraria
                    no token cujas duas linhas são iguais — sorteio de 122 bits
                    permite, e uma etiqueta com uma linha faltando é
                    identificação de material pela metade. */}
                {linhasDoToken(volume.token).map((linha, i) => (
                  <span className="et__url-linha" key={i}>
                    {linha}
                  </span>
                ))}
              </p>
            </div>
          )}
        </div>

        {/* ⚠️ AQUI HAVIA A FAIXA PRETA DE SITUAÇÃO, E ELA NÃO VOLTA.
            Ver o cabeçalho deste componente: a etiqueta não é trocada na
            liberação, então ela não pode afirmar o status. O papel
            identifica; a situação viva mora no sistema, que o QR abre, e o
            controle no chão é a área física de quarentena separada. */}

        {/* ── a marca de ambiente, NO RODAPÉ ───────────────────────────
            Ela estava no TOPO e desceu para cá — pedido do PO. O topo é
            para IDENTIFICAR o material; a marca de ambiente é RESSALVA
            sobre o sistema que imprimiu o papel, e ressalva não disputa a
            primeira leitura com o código do lote.

            ⚠️ ELA NÃO PODE SIMPLESMENTE SUMIR, e isso é regulatório: uma
            etiqueta impressa sem ela PARECE oficial, e vai colada num
            tambor longe da tela que dizia o contrário (ADR-0004, ADR-0006
            item 4). Este projeto já teve exatamente esse defeito — um
            `!important` apagava a faixa da impressão, e a palavra sumia do
            papel estando visível na tela.

            🔴 Removê-la POR COMPLETO é decisão do PO, e não foi tomada.

            O gatilho é o AMBIENTE, e não uma constante: em produção
            validada ela some sozinha, sem ninguém precisar lembrar. O
            mecanismo é o mesmo da `FaixaAmbiente` — `usarAmbiente()`, e
            só `producao` esconde. Ambiente desconhecido EXIBE: na dúvida,
            avisa, porque errar essa variável faz um sistema não validado
            se passar por validado. */}
        {ambiente !== "producao" && (
          <p className="et__ambiente">SISTEMA EM VALIDAÇÃO · NÃO É O REGISTRO OFICIAL</p>
        )}
      </div>
    </div>
  );
}

/*
 * ⚠️ AQUI MORAVA `formatarToken`, E ELE IMPRIMIU DEFEITO NO PAPEL.
 *
 *   token.length === 8 ? `${token.slice(0, 4)}-${token.slice(4)}` : token
 *
 * O `if` reconhecia só o comprimento antigo. Quando a `V14` levou o token para
 * 32 hexadecimais, ele deixou de pegar — em silêncio, que é o modo de falha que
 * esta etiqueta existe para não ter — e a folha passou a sair com 32 glifos
 * corridos, sem separação nenhuma, num campo desenhado para nove.
 *
 * A regra foi para `packages/ui/src/token.ts` e deixou de perguntar o
 * comprimento: ela agrupa de quatro em quatro, seja qual for. Um formato novo
 * não a quebra de novo — e um token curto de registro histórico continua
 * desenhando (`K7M4-P2XQ`), em vez de derrubar a etiqueta.
 */

/**
 * A data vem em ISO do servidor e é formatada só na apresentação.
 *
 * <p>Montada à mão, sem fuso: a API trafega a data do calendário
 * (`2026-08-12`), e passá-la por `new Date()` a interpretaria como meia-noite
 * UTC — que no Brasil é o dia anterior. Numa etiqueta de validade, um dia de
 * diferença não é detalhe.
 */
function formatar(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}
